import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import suburbsData from "../../../data/us_addresses.json";
import JobWorkerStepsComponent from "./JobWorkerStepsComponent";
import config from "../../../config/config";
import { useJobCreate } from "../../../context/JobCreateContext";
import {
  handleUseCurrentLocation,
  handleSuburbChange,
  handleClearSuburb,
} from "../../../util_functions/helpers";
import { FaCrosshairs, FaSpinner, FaSearch } from "react-icons/fa";

const JobCreateComponent = () => {
  const {
    saveJobSelections,
    getJobCreateIdFromCookies,
    jobSelections,
    fetchJobSelectionsById,
    clearJobSelections,
  } = useJobCreate();

  const [suburbValue, setSuburbValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]); 
  const [pendingAddFromCurrent, setPendingAddFromCurrent] = useState(false);  
  const [addressValidationMessage, setAddressValidationMessage] = useState("");
  const [validationMessageClass, setValidationMessageClass] = useState("");
  const [isLocating, setIsLocating] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const stepMatch = location.pathname.match(/standard\/step-(\d+)/);
  const inStepsPhase = stepMatch !== null;

  const inputRef = useRef(null);

  const showSuggestions = suggestions.length > 0;

  const jobCreationMode = location?.state?.jobCreationMode || "manual";
  const isTaskSenseMode = jobCreationMode === "tasksense";

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!inStepsPhase) inputRef.current?.focus();
  }, [inStepsPhase]);

  useEffect(() => {
    const jobCreateId = getJobCreateIdFromCookies();
    if (jobCreateId) {
      fetchJobSelectionsById(jobCreateId)
        .then((fetched) => {
          if (Array.isArray(fetched?.selectedLocations) && fetched.selectedLocations.length) {
            setSelectedLocations(fetched.selectedLocations);
            setSuburbValue("");
          } else if (fetched?.suburbValue) {
            setSuburbValue(fetched.suburbValue);
          }
        })
        .catch((error) => {
          console.error("[JobCreateComponent] Error fetching job by ID:", error);
        })
        .finally(() => {
          if (!inStepsPhase && inputRef.current) inputRef.current.focus();
        });
    } else {
      if (!inStepsPhase && inputRef.current) inputRef.current.focus();
    }
  }, [getJobCreateIdFromCookies, fetchJobSelectionsById, inStepsPhase]);

  const addLocation = (loc) => {
    const normalized = loc.trim().toLowerCase();
    if (!normalized) return;
    setSelectedLocations((prev) =>
      prev.some((p) => p.toLowerCase() === normalized) ? prev : [...prev, loc]
    );
    setSuggestions([]);
    setSuburbValue("");
    setAddressValidationMessage("");
    setValidationMessageClass("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeLocation = (loc) => {
    setSelectedLocations((prev) => prev.filter((p) => p !== loc));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (pendingAddFromCurrent && suburbValue.trim()) {
      addLocation(suburbValue);
      setPendingAddFromCurrent(false);
    }
  }, [pendingAddFromCurrent, suburbValue]);

  const arraysEqualCI = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const A = a.map((x) => x.toLowerCase()).sort();
    const B = b.map((x) => x.toLowerCase()).sort();
    return A.every((v, i) => v === B[i]);
  };

  // const handleSearchClick = async (e) => {
  //   e.preventDefault();
  //   setAddressValidationMessage("");
  //   setValidationMessageClass("");

  //   const candidateLocations =
  //     selectedLocations.length > 0
  //       ? selectedLocations
  //       : suburbValue.trim()
  //         ? [suburbValue.trim()]
  //         : [];

  //   if (candidateLocations.length === 0) {
  //     setAddressValidationMessage("Please select at least one location.");
  //     setValidationMessageClass("address-invalid");
  //     inputRef.current?.focus();
  //     return;
  //   }

  //   const allValid = candidateLocations.every((loc) =>
  //     suburbsData.Suburbs_Zipcodes.some(
  //       (s) =>
  //         `${s.Post_City} ${s.State} ${s.Zip_Code}`.toLowerCase().trim() ===
  //         loc.toLowerCase().trim()
  //     )
  //   );
  //   if (!allValid) {
  //     setAddressValidationMessage("One or more locations are invalid. Please select from suggestions.");
  //     setValidationMessageClass("address-invalid");
  //     return;
  //   }

  //   const existingId = getJobCreateIdFromCookies();

  //   const previous = Array.isArray(jobSelections?.selectedLocations) && jobSelections.selectedLocations.length
  //     ? jobSelections.selectedLocations
  //     : jobSelections?.suburbValue
  //       ? [jobSelections.suburbValue]
  //       : [];

  //   if (existingId && arraysEqualCI(previous, candidateLocations)) {
  //     let path = location.pathname;
  //     const m = path.match(/(step-\d+)/);
  //     const step = m ? `/${m[0]}` : "/step-1";
  //     if (path.includes("standard")) path = path.replace("/standard", `/standard${step}`);
  //     else if (path.includes("urgent")) path = path.replace("/urgent", `/urgent${step}`);
  //     else path += `/standard${step}`;

  //     return navigate(path, {
  //       state: {
  //         suburbValue: candidateLocations[0],
  //         selectedLocations: candidateLocations,
  //         jobCreateId: existingId,
  //       },
  //     });
  //   }

  //   if (existingId) {
  //     clearJobSelections();
  //     console.log("Cleared leftover draft:", existingId);
  //   }

  //   try {
  //     const payload = {
  //       suburbValue: candidateLocations[0],
  //       selectedLocations: candidateLocations,
  //     };
  //     const id = await saveJobSelections(payload, true);

  //     let path = location.pathname;
  //     const m = path.match(/(step-\d+)/);
  //     const step = m ? `/${m[0]}` : "/step-1";
  //     if (path.includes("standard")) path = path.replace("/standard", `/standard${step}`);
  //     else if (path.includes("urgent")) path = path.replace("/urgent", `/urgent${step}`);
  //     else path += `/standard${step}`;

  //     navigate(path, {
  //       state: {
  //         suburbValue: candidateLocations[0],
  //         selectedLocations: candidateLocations,
  //         jobCreateId: id,
  //       },
  //     });
  //   } catch (err) {
  //     console.error("[handleSearchClick] ERROR:", err);
  //     setAddressValidationMessage("Something went wrong. Please try again.");
  //     setValidationMessageClass("address-invalid");
  //   }
  // };

  const handleSearchClick = async (e) => {
  e.preventDefault();

  setAddressValidationMessage("");
  setValidationMessageClass("");

  const mode = location?.state?.jobCreationMode || "manual"; 

  const candidateLocations =
    selectedLocations.length > 0
      ? selectedLocations
      : suburbValue.trim()
      ? [suburbValue.trim()]
      : [];

  if (candidateLocations.length === 0) {
    setAddressValidationMessage("Please select at least one location.");
    setValidationMessageClass("address-invalid");
    inputRef.current?.focus();
    return;
  }

  const allValid = candidateLocations.every((loc) =>
    suburbsData.Suburbs_Zipcodes.some(
      (s) =>
        `${s.Post_City} ${s.State} ${s.Zip_Code}`.toLowerCase().trim() ===
        loc.toLowerCase().trim()
    )
  );

  if (!allValid) {
    setAddressValidationMessage(
      "One or more locations are invalid. Please select from suggestions."
    );
    setValidationMessageClass("address-invalid");
    return;
  }

  const existingId = getJobCreateIdFromCookies();

  const previous =
    Array.isArray(jobSelections?.selectedLocations) &&
    jobSelections.selectedLocations.length
      ? jobSelections.selectedLocations
      : jobSelections?.suburbValue
      ? [jobSelections.suburbValue]
      : [];

  const buildNextPath = () => {
  const isUrgent = location.pathname.includes("/urgent");
  const isStandard = location.pathname.includes("/standard");

  const manualBase = isUrgent
    ? "/account/fs-dashboard/my-jobs/new/urgent"
    : "/account/fs-dashboard/my-jobs/new/standard";

  if (mode === "tasksense") {
    return "/account/fs-dashboard/my-jobs/new/tasksense/step-ai";
  }

  const stepMatch = location.pathname.match(/(step-\d+)/);
  const currentStep = stepMatch ? stepMatch[1] : null;

  return currentStep ? `${manualBase}/${currentStep}` : `${manualBase}/step-1`;
};



  if (existingId && arraysEqualCI(previous, candidateLocations)) {
    const nextPath = buildNextPath();
    return navigate(nextPath, {
      state: {
        suburbValue: candidateLocations[0],
        selectedLocations: candidateLocations,
        jobCreateId: existingId,
        jobCreationMode: mode,
      },
    });
  }

  if (existingId) {
    clearJobSelections();
    console.log("Cleared leftover draft:", existingId);
  }

  try {
    const payload = {
      suburbValue: candidateLocations[0],
      selectedLocations: candidateLocations,
    };

    const id = await saveJobSelections(payload, true);

    const nextPath = buildNextPath();

    navigate(nextPath, {
      state: {
        suburbValue: candidateLocations[0],
        selectedLocations: candidateLocations,
        jobCreateId: id,
        jobCreationMode: mode,
      },
    });
  } catch (err) {
    console.error("[handleSearchClick] ERROR:", err);
    setAddressValidationMessage("Something went wrong. Please try again.");
    setValidationMessageClass("address-invalid");
  }
};


  if (inStepsPhase) {
    return (
      <JobWorkerStepsComponent
        onGoBackToSearch={() =>
          navigate(`${config.BASE_FS_ROUTE}/my-jobs/new/standard`)
        }
        suburb={suburbValue}
      />
    );
  }

  return (
    <div className="search-workers-container">
      <div className="search-support-workers">
        <div className="validation-message-container">
          {addressValidationMessage && (
            <div className={validationMessageClass}>{addressValidationMessage}</div>
          )}
        </div>

        <div className="job-creation-mode-label">
  <span
    className={`job-creation-mode-badge ${
      isTaskSenseMode ? "ai" : "manual"
    }`}
  >
    {isTaskSenseMode ? "AI Mode" : "Manual Mode"}
  </span>

  <span className="job-creation-mode-text">
    {isTaskSenseMode
      ? "Crafting your job with TaskSense AI"
      : "You are posting a job manually"}
  </span>
</div>


        <button
          type="button"
          className="use-location-button"
          onClick={() => {
            setPendingAddFromCurrent(true);
            handleUseCurrentLocation({
              setSuburbValue,
              setAddressValidationMessage,
              setValidationMessageClass,
              setIsLocating,
            });
          }}
          disabled={isLocating}
        >
          {isLocating ? (
            <>
              <FaSpinner className="icon spin" />
              Locating…
            </>
          ) : (
            <>
              <FaCrosshairs className="icon" />
              Use current location
            </>
          )}
        </button>

        <form>
          <div className="search-input-container">
            <div className="search-input-button-wrapper">
              <div className="search-input-with-icon">
                <FaSearch className="location-search-icon" />
                <input
                  className="search-location-input"
                  ref={inputRef}
                  type="text"
                  value={suburbValue}
                  onChange={(e) => handleSuburbChange(e, setSuburbValue, setSuggestions)}
                  placeholder={isLocating ? "Locating your city/ZIP…" : "Type City or ZIP Code then select…"}
                  disabled={isLocating}
                  aria-disabled={isLocating}
                  aria-busy={isLocating}
                />

                {suburbValue && (
                  <span
                    onClick={() =>
                      handleClearSuburb(
                        setSuburbValue,
                        setSuggestions,
                        setAddressValidationMessage,
                        inputRef
                      )
                    }
                    className="search-clear-icon"
                  >
                    X
                  </span>
                )}
              </div>
            </div>

            <button
              className="search-location-button"
              onClick={handleSearchClick}
              disabled={selectedLocations.length === 0 && !suburbValue.trim()}
            >
              Next
            </button>
          </div>

          {showSuggestions && (
            <div className="search-location-suggestions">
              <div>
                <ul>
                  {suggestions.map((address, index) => {
                    const formatted = `${address.Post_City} ${address.State} ${address.Zip_Code}`;
                    return (
                      <li
                        key={`${formatted}-${index}`}
                        onClick={() => addLocation(formatted)}
                      >
                        {formatted}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {!showSuggestions && selectedLocations.length > 0 && (
            <div className="selected-locations">
              <div className="selected-locations-title">Selected locations</div>
              <div class="selected-locations-scroll">
              <ul>
                {selectedLocations.map((loc) => (
                  <li key={loc} className="selected-location-item">
                    <span>{loc}</span>
                    <button
                      type="button"
                      className="remove-selected-location"
                      onClick={() => removeLocation(loc)}
                      aria-label={`Remove ${loc}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default JobCreateComponent;
